import React, { FC, useState } from 'react'
import { useUser } from '@/providers/userProvider'
import styled, { useTheme } from 'styled-components'
import { transparentize } from 'polished'
import useTranslation from 'next-translate/useTranslation'
import { Sdk } from '@/network/graphql.g'
import { notifySuccess } from '@/core/toast'
import useGraphQl from '../../../hooks/useGraphQl'
import { Box } from '@/components/atoms/Grid'
import useDevice from '../../../hooks/useDevice'
import { validFor } from '@/theme/validFor'
import { ID } from 'graphql-ws'
import { ExclamationIconDefault } from '@/components/atoms/icons/ExclamationIconDefault'
import { IconButton } from '@/components/molecules/iconButtons/iconButton'

enum ButtonType {
  RemoveProduct = 'removeProduct',
  RemoveProductFromCategoryTree = 'removeProductFromCategoryTree',
  RemoveProductFromCategory = 'removeProductFromCategory'
}

type RemoveProductButtonsProps = {
  productId: ID
  mainCategoryId: string
}

const RemoveProductButtons: FC<RemoveProductButtonsProps> = ({
  productId,
  mainCategoryId
}) => {
  const { canManageProducts } = useUser()
  const { t } = useTranslation('common')
  const { requestNotifyError } = useGraphQl()
  const { isDesktop } = useDevice()
  const [countdown, setCountdown] = useState<number | null>(null)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null)
  const [showRemoveMenu, setShowRemoveMenu] = useState<boolean>(false)
  const [activeCountdownButton, setActiveCountdownButton] =
    useState<string | null>(null)
  const theme = useTheme()

  const buttonConfigs = [
    {
      id: 1,
      buttonType: ButtonType.RemoveProduct,
      buttonLabelKey: 'AdditionalInfo.removeButtonProduct'
    },
    {
      id: 2,
      buttonType: ButtonType.RemoveProductFromCategoryTree,
      buttonLabelKey: 'AdditionalInfo.removeButtonAllCategory'
    },
    {
      id: 3,
      buttonType: ButtonType.RemoveProductFromCategory,
      buttonLabelKey: 'AdditionalInfo.removeButtonCategory'
    }
  ]

  const deleteProduct = async (productId: string) => {
    const { response, success } = await requestNotifyError((sdk: Sdk) => ({
      method: sdk.disableProduct,
      variables: {
        product: productId
      }
    }))
    const responseMessage = response?.disableProduct?.responseMessage
    if (response && success) {
      notifySuccess(responseMessage)
    }
  }

  const removeProductFromCategoryTree = async (
    productId: string,
    mainCategoryId: string
  ) => {
    const { response, success } = await requestNotifyError((sdk: Sdk) => ({
      method: sdk.removeProductFromCategoryTree,
      variables: {
        product: productId,
        category: mainCategoryId
      }
    }))
    const responseMessage =
      response?.removeProductFromCategoryTree?.responseMessage
    if (response && success) {
      notifySuccess(responseMessage)
    }
  }

  const removeProductFromCategory = async (
    productId: string,
    mainCategoryId: string
  ) => {
    const { response, success } = await requestNotifyError((sdk: Sdk) => ({
      method: sdk.removeProductFromCategory,
      variables: {
        product: productId,
        category: mainCategoryId
      }
    }))
    const responseMessage = response?.removeProductFromCategory?.responseMessage
    if (response && success) {
      notifySuccess(responseMessage)
    }
  }

  const startCountdown = (buttonId: string, actionCallback: () => void) => {
    if (activeCountdownButton === buttonId) {
      if (intervalId) {
        clearInterval(intervalId)
      }
      setCountdown(null)
      setActiveCountdownButton(null)
      setIntervalId(null)
      return
    }

    let counter = 3
    setCountdown(counter)
    setActiveCountdownButton(buttonId)

    const id = setInterval(() => {
      counter -= 1
      setCountdown(counter)

      if (counter === 0) {
        clearInterval(id)
        setCountdown(null)
        setActiveCountdownButton(null)
        setIntervalId(null)
        actionCallback()
      }
    }, 1000)

    setIntervalId(id)
  }

  const handleMenuOptionClick = (buttonId: string, action: () => void) => {
    startCountdown(buttonId, action)
  }

  const toggleRemoveMenu = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
    setShowRemoveMenu(!showRemoveMenu)
  }

  return (
    <>
      {canManageProducts && (
        <>
          {isDesktop ? (
            <RemoveProductButton
              onClick={toggleRemoveMenu}
              title={t('AdditionalInfo.removeButton')}
              IconElement={
                <ExclamationIconDefault
                  width={21}
                  height={21}
                  color={theme.colors.background}
                />
              }
            />
          ) : (
            <RemoveProductButtonMobileTablet onClick={toggleRemoveMenu}>
              <ExclamationIconDefault
                width={21}
                height={21}
                color={theme.colors.background}
              />
            </RemoveProductButtonMobileTablet>
          )}
          {showRemoveMenu && (
            <RemoveMenu isDesktop={isDesktop}>
              {buttonConfigs.map(({ id, buttonType, buttonLabelKey }) => {
                const action = () => {
                  switch (buttonType) {
                    case ButtonType.RemoveProduct:
                      return deleteProduct(productId)
                    case ButtonType.RemoveProductFromCategoryTree:
                      return removeProductFromCategoryTree(
                        productId,
                        mainCategoryId
                      )
                    case ButtonType.RemoveProductFromCategory:
                      return removeProductFromCategory(
                        productId,
                        mainCategoryId
                      )
                    default:
                      return
                  }
                }

                return (
                  <RemoveMenuButton
                    key={id}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleMenuOptionClick(buttonType, action)
                    }}
                  >
                    {activeCountdownButton === buttonType
                      ? `${countdown}s`
                      : t(buttonLabelKey)}
                  </RemoveMenuButton>
                )
              })}
            </RemoveMenu>
          )}
        </>
      )}
    </>
  )
}

export default RemoveProductButtons

const RemoveMenu = styled(Box)`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 4px;
  padding: ${(props) => (props.isDesktop ? '10px' : '5px')};
  position: absolute;
  bottom: ${(props) => props.isDesktop && '100px'};
  left: ${(props) => (props.isDesktop ? '20px' : '5px')};
  right: ${(props) => (props.isDesktop ? '20px' : '5px')};
  top: ${(props) => !props.isDesktop && '60px'};
  z-index: 10;
`

const RemoveProductButton = styled(IconButton)`
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  margin: 0 3px 14px 3px;
  height: 35px;
  z-index: 10;
  border: none;
  &:hover {
    background-color: ${(props) =>
      transparentize(0.2, props.theme.colors.accent)};
  }
`

const RemoveMenuButton = styled.button`
  margin: 5px 0;
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.background};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  z-index: 10;
  height: ${(props) => (props.isDesktop ? '40px' : '30px')};
  ${validFor.mobile`
    font-size: 10px;
  `}
  &:hover {
    background-color: ${(props) =>
      transparentize(0.2, props.theme.colors.accent)};
  }
`

const RemoveProductButtonMobileTablet = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  background-color: ${(props) => props.theme.colors.accent};
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
`
